import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SubscriptionModal from '../components/SubscriptionModal';
import SubscriptionPlanCard from '../components/SubscriptionPlanCard';
import { 
  useSubscriptions, 
  useSubscriptionPlans, 
  useSubscriptionStatistics,
  useExpiringTrials,
  useUpcomingRenewals 
} from '../hooks/useSubscriptions';

const { 
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiPause, FiPlay, FiRefreshCw,
  FiCreditCard, FiCalendar, FiUsers, FiTrendingUp, FiClock, FiAlertTriangle,
  FiCheckCircle, FiGrid, FiList, FiArrowUp, FiArrowDown, FiFilter,
  FiDollarSign, FiStar, FiTarget, FiSettings, FiUpgrade, FiX
} = FiIcons;

const Subscriptions = () => {
  const { 
    subscriptions, 
    loading, 
    error, 
    createSubscription, 
    updateSubscription,
    cancelSubscription,
    pauseSubscription,
    resumeSubscription,
    upgradeSubscription,
    processRenewal,
    convertTrialToSubscription,
    refetch 
  } = useSubscriptions();
  
  const { plans } = useSubscriptionPlans();
  const { stats } = useSubscriptionStatistics();
  const { trials } = useExpiringTrials();
  const { renewals } = useUpcomingRenewals();
  
  const [showModal, setShowModal] = useState(false);
  const [showPlansView, setShowPlansView] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filter and search subscriptions
  const filteredSubscriptions = subscriptions
    .filter(subscription => {
      const matchesSearch = 
        subscription.client?.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subscription.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subscription.plan?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterBy === 'all') return matchesSearch;
      if (filterBy === 'active') return matchesSearch && subscription.status === 'active';
      if (filterBy === 'trial') return matchesSearch && subscription.status === 'trial';
      if (filterBy === 'paused') return matchesSearch && subscription.status === 'paused';
      if (filterBy === 'cancelled') return matchesSearch && subscription.status === 'cancelled';
      if (filterBy === 'expiring_trials') return matchesSearch && subscription.is_trial && trials.some(t => t.id === subscription.id);
      if (filterBy === 'upcoming_renewals') return matchesSearch && renewals.some(r => r.id === subscription.id);
      
      return matchesSearch;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'client':
          aValue = a.client?.business_name || '';
          bValue = b.client?.business_name || '';
          break;
        case 'plan':
          aValue = a.plan?.name || '';
          bValue = b.plan?.name || '';
          break;
        case 'price':
          aValue = a.custom_price || a.plan?.price || 0;
          bValue = b.custom_price || b.plan?.price || 0;
          break;
        case 'next_billing':
          aValue = new Date(a.next_billing_date || 0);
          bValue = new Date(b.next_billing_date || 0);
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          break;
        default:
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
      }
      
      if (typeof aValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

  const handleCreateSubscription = async (subscriptionData) => {
    const result = await createSubscription(subscriptionData);
    if (result.success) {
      setShowModal(false);
      setSelectedSubscription(null);
    }
    return result;
  };

  const handleUpdateSubscription = async (subscriptionData) => {
    if (selectedSubscription) {
      const result = await updateSubscription(selectedSubscription.id, subscriptionData);
      if (result.success) {
        setShowModal(false);
        setSelectedSubscription(null);
      }
      return result;
    }
  };

  const handleCancelSubscription = async (id) => {
    const reason = prompt('Please provide a reason for cancellation (optional):');
    if (reason !== null) {
      await cancelSubscription(id, reason);
    }
  };

  const handlePauseSubscription = async (id) => {
    const reason = prompt('Please provide a reason for pausing (optional):');
    if (reason !== null) {
      await pauseSubscription(id, reason);
    }
  };

  const handleConvertTrial = async (id) => {
    if (window.confirm('Convert this trial to a paid subscription?')) {
      await convertTrialToSubscription(id);
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'trial': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'paused': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'expired': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDaysUntilExpiry = (dateString) => {
    const days = Math.ceil((new Date(dateString) - new Date()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SafeIcon icon={FiRefreshCw} className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading subscriptions...</span>
      </div>
    );
  }

  if (showPlansView) {
    return (
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Subscription Plans</h1>
            <p className="text-gray-600">Choose the perfect plan for your clients</p>
          </div>
          
          <button
            onClick={() => setShowPlansView(false)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
            <span>Back to Subscriptions</span>
          </button>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <SubscriptionPlanCard 
                plan={plan} 
                onSelect={() => {
                  setSelectedSubscription(null);
                  setShowModal(true);
                  setShowPlansView(false);
                }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Subscription Management</h1>
          <p className="text-gray-600">
            Manage recurring billing, trials, and subscription lifecycles
          </p>
        </div>
        
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <button
            onClick={() => setShowPlansView(true)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <SafeIcon icon={FiTarget} className="w-4 h-4" />
            <span>View Plans</span>
          </button>
          
          <button
            onClick={refetch}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          
          <button
            onClick={() => {
              setSelectedSubscription(null);
              setShowModal(true);
            }}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>New Subscription</span>
          </button>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Monthly Recurring Revenue</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(stats.monthly_recurring_revenue || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Subscriptions</p>
              <p className="text-2xl font-bold text-blue-600">{stats.active_subscriptions || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Trial Subscriptions</p>
              <p className="text-2xl font-bold text-purple-600">{stats.trial_subscriptions || 0}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiClock} className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Expiring Trials</p>
              <p className="text-2xl font-bold text-orange-600">{stats.expiring_trials || 0}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions for Expiring Trials and Upcoming Renewals */}
      {(trials.length > 0 || renewals.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          {trials.length > 0 && (
            <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 mr-2 text-orange-600" />
                Expiring Trials ({trials.length})
              </h3>
              <div className="space-y-3">
                {trials.slice(0, 3).map(trial => (
                  <div key={trial.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-300">
                    <div>
                      <p className="font-medium text-gray-800">{trial.client?.business_name}</p>
                      <p className="text-sm text-gray-600">{trial.plan?.name}</p>
                      <p className="text-xs text-orange-600">Expires in {getDaysUntilExpiry(trial.trial_end)} days</p>
                    </div>
                    <button
                      onClick={() => handleConvertTrial(trial.id)}
                      className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700 transition-colors duration-200"
                    >
                      Convert
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {renewals.length > 0 && (
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <SafeIcon icon={FiRefreshCw} className="w-5 h-5 mr-2 text-blue-600" />
                Upcoming Renewals ({renewals.length})
              </h3>
              <div className="space-y-3">
                {renewals.slice(0, 3).map(renewal => (
                  <div key={renewal.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-300">
                    <div>
                      <p className="font-medium text-gray-800">{renewal.client?.business_name}</p>
                      <p className="text-sm text-gray-600">{renewal.plan?.name}</p>
                      <p className="text-xs text-blue-600">Renews {formatDate(renewal.next_billing_date)}</p>
                    </div>
                    <button
                      onClick={() => processRenewal(renewal.id)}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors duration-200"
                    >
                      Process
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Search, Filter, and Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative flex-1 min-w-80">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search subscriptions by client or plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Subscriptions ({subscriptions.length})</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
              <option value="expiring_trials">Expiring Trials</option>
              <option value="upcoming_renewals">Upcoming Renewals</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="created_at">Sort by Created Date</option>
              <option value="next_billing">Sort by Next Billing</option>
              <option value="client">Sort by Client</option>
              <option value="plan">Sort by Plan</option>
              <option value="price">Sort by Price</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                viewMode === 'grid' ? 'bg-blue-100 text-blue-600 border border-blue-200' : 'hover:bg-gray-100 border border-gray-300'
              }`}
            >
              <SafeIcon icon={FiGrid} className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                viewMode === 'list' ? 'bg-blue-100 text-blue-600 border border-blue-200' : 'hover:bg-gray-100 border border-gray-300'
              }`}
            >
              <SafeIcon icon={FiList} className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {filteredSubscriptions.length} of {subscriptions.length} subscriptions
          </p>
          
          {error && (
            <div className="text-red-600 text-sm">
              {error} (Running in demo mode)
            </div>
          )}
        </div>
      </motion.div>

      {/* Subscriptions Display */}
      {filteredSubscriptions.length > 0 ? (
        <div className={viewMode === 'grid' ? 
          'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' : 
          'space-y-4'
        }>
          {filteredSubscriptions.map((subscription, index) => (
            <motion.div
              key={subscription.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className={`bg-white rounded-xl p-6 shadow-soft hover:shadow-medium transition-all duration-300 border border-gray-200 ${
                viewMode === 'list' ? 'flex items-center justify-between' : ''
              }`}
            >
              <div className={`${viewMode === 'list' ? 'flex-1' : ''}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">{subscription.client?.business_name}</h3>
                    <p className="text-gray-600 mb-2">{subscription.plan?.name}</p>
                    <div className="flex items-center space-x-2 flex-wrap gap-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(subscription.status)}`}>
                        {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                      </span>
                      {subscription.is_trial && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium border bg-blue-100 text-blue-800 border-blue-200">
                          Trial: {getDaysUntilExpiry(subscription.trial_end)} days left
                        </span>
                      )}
                      {subscription.auto_renew && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium border bg-green-100 text-green-800 border-green-200">
                          Auto-renew
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {viewMode === 'grid' && (
                    <div className="flex items-center space-x-1 ml-2">
                      <button 
                        onClick={() => {
                          setSelectedSubscription(subscription);
                          setShowModal(true);
                        }}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                        title="Edit Subscription"
                      >
                        <SafeIcon icon={FiEdit2} className="w-4 h-4 text-blue-600" />
                      </button>
                      
                      {subscription.status === 'active' && (
                        <button 
                          onClick={() => handlePauseSubscription(subscription.id)}
                          className="p-2 hover:bg-yellow-50 rounded-lg transition-colors duration-200"
                          title="Pause Subscription"
                        >
                          <SafeIcon icon={FiPause} className="w-4 h-4 text-yellow-600" />
                        </button>
                      )}
                      
                      {subscription.status === 'paused' && (
                        <button 
                          onClick={() => resumeSubscription(subscription.id)}
                          className="p-2 hover:bg-green-50 rounded-lg transition-colors duration-200"
                          title="Resume Subscription"
                        >
                          <SafeIcon icon={FiPlay} className="w-4 h-4 text-green-600" />
                        </button>
                      )}
                      
                      {subscription.status === 'trial' && (
                        <button 
                          onClick={() => handleConvertTrial(subscription.id)}
                          className="p-2 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                          title="Convert Trial"
                        >
                          <SafeIcon icon={FiUpgrade} className="w-4 h-4 text-purple-600" />
                        </button>
                      )}
                      
                      <button 
                        onClick={() => handleCancelSubscription(subscription.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Cancel Subscription"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-gray-400" />
                    <span>
                      Price: <strong>{formatCurrency(subscription.custom_price || subscription.plan?.price)}</strong>
                      /{subscription.plan?.billing_cycle}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                    <span>Next Billing: {formatDate(subscription.next_billing_date)}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiCreditCard} className="w-4 h-4 text-gray-400" />
                    <span>Payment: {subscription.payment_method?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  </div>
                  {subscription.discount_percentage > 0 && (
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiStar} className="w-4 h-4 text-green-400" />
                      <span className="text-green-600">Discount: {subscription.discount_percentage}% off</span>
                    </div>
                  )}
                </div>

                {viewMode === 'grid' && subscription.notes && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500">{subscription.notes}</p>
                  </div>
                )}
              </div>

              {viewMode === 'list' && (
                <div className="flex items-center space-x-4 ml-6">
                  <div className="text-right min-w-32">
                    <p className="text-lg font-bold text-gray-800">
                      {formatCurrency(subscription.custom_price || subscription.plan?.price)}
                    </p>
                    <p className="text-xs text-gray-500">{formatDate(subscription.next_billing_date)}</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button 
                      onClick={() => {
                        setSelectedSubscription(subscription);
                        setShowModal(true);
                      }}
                      className="p-2 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                      title="Edit Subscription"
                    >
                      <SafeIcon icon={FiEdit2} className="w-4 h-4 text-blue-600" />
                    </button>
                    
                    {subscription.status === 'active' ? (
                      <button 
                        onClick={() => handlePauseSubscription(subscription.id)}
                        className="p-2 hover:bg-yellow-50 rounded-lg transition-colors duration-200"
                        title="Pause Subscription"
                      >
                        <SafeIcon icon={FiPause} className="w-4 h-4 text-yellow-600" />
                      </button>
                    ) : subscription.status === 'paused' ? (
                      <button 
                        onClick={() => resumeSubscription(subscription.id)}
                        className="p-2 hover:bg-green-50 rounded-lg transition-colors duration-200"
                        title="Resume Subscription"
                      >
                        <SafeIcon icon={FiPlay} className="w-4 h-4 text-green-600" />
                      </button>
                    ) : subscription.status === 'trial' ? (
                      <button 
                        onClick={() => handleConvertTrial(subscription.id)}
                        className="p-2 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                        title="Convert Trial"
                      >
                        <SafeIcon icon={FiUpgrade} className="w-4 h-4 text-purple-600" />
                      </button>
                    ) : null}
                    
                    <button 
                      onClick={() => handleCancelSubscription(subscription.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      title="Cancel Subscription"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-12 shadow-soft text-center border border-gray-200"
        >
          <SafeIcon icon={FiCreditCard} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            {searchTerm || filterBy !== 'all' ? 'No subscriptions found' : 'No subscriptions yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || filterBy !== 'all' 
              ? 'Try adjusting your search or filter criteria'
              : 'Start by creating your first subscription to manage recurring billing'
            }
          </p>
          {(!searchTerm && filterBy === 'all') && (
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={() => setShowPlansView(true)}
                className="bg-blue-100 text-blue-700 px-6 py-2 rounded-lg transition-colors duration-200 hover:bg-blue-200"
              >
                View Plans
              </button>
              <button
                onClick={() => {
                  setSelectedSubscription(null);
                  setShowModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
              >
                Create Subscription
              </button>
            </div>
          )}
        </motion.div>
      )}

      {/* Modal */}
      <SubscriptionModal 
        isOpen={showModal} 
        onClose={() => {
          setShowModal(false);
          setSelectedSubscription(null);
        }}
        subscription={selectedSubscription}
        onSubmit={selectedSubscription ? handleUpdateSubscription : handleCreateSubscription}
      />
    </div>
  );
};

export default Subscriptions;